// 🔧 points_scorecard_form.js
// ✅ Purpose: Reusable Points / Scorecard scoring form
// ✅ Features:
// - Displays all scoring factors for one entry
// - Allows voter/judge to enter points per scoring factor
// - Prevents scoring above each factor max
// - Shows key highlight checkboxes per scoring factor
// - Tracks which key highlights were checked by the voter
// - Shows running total out of maxBallotPoints
// - Sends updated scorecard data back to parent page
// - Keeps large Points Voting UI out of vote.js
// - Fully block-commented for clarity and future devs

import React from "react";

function PointsScorecardForm({
  scorecard,
  setScorecard,
  maxBallotPoints = 100,
  disabled = false,
}) {

// ✅ Calculates live total for both Simple Mode and Scorecard Mode
// ✅ Simple Mode uses score.pointsAwarded
// ✅ Scorecard Mode rolls up scored highlights/criteria
const currentTotal = scorecard.reduce((sum, score) => {
  const hasScoredHighlights = (score.checkedHighlights || []).some(
    (highlight) => Number(highlight.maxPoints || 0) > 0
  );

  if (hasScoredHighlights) {
    return (
      sum +
      (score.checkedHighlights || []).reduce(
        (highlightSum, highlight) =>
          highlightSum + Number(highlight.pointsAwarded || 0),
        0
      )
    );
  }

  return sum + Number(score.pointsAwarded || 0);
}, 0);

const pointsRemaining = maxBallotPoints - currentTotal;
  function handlePointsChange(factorId, value) {
    setScorecard((currentScorecard) =>
      currentScorecard.map((score) => {
        if (score.factorId !== factorId) {
          return score;
        }

        const enteredPoints = Number(value);
        const safePoints = Number.isNaN(enteredPoints) ? "" : enteredPoints;
        const maxPoints = Number(score.maxPoints || 0);

        return {
          ...score,
          pointsAwarded:
            safePoints === ""
              ? ""
              : Math.min(Math.max(safePoints, 0), maxPoints),
        };
      })
    );
  }

  function handleHighlightToggle(factorId, highlightId) {
    setScorecard((currentScorecard) =>
      currentScorecard.map((score) => {
        if (score.factorId !== factorId) {
          return score;
        }

        return {
          ...score,
          checkedHighlights: (score.checkedHighlights || []).map(
            (highlight) => {
              if (highlight.highlightId !== highlightId) {
                return highlight;
              }

              return {
                ...highlight,
                checked: !highlight.checked,
              };
            }
          ),
        };
      })
    );
  }

  function handleHighlightPointsChange(
    factorId,
    highlightId,
    value
  ) {
    setScorecard((currentScorecard) =>
      currentScorecard.map((score) => {
        if (score.factorId !== factorId) {
          return score;
        }

        const updatedHighlights =
          (score.checkedHighlights || []).map(
            (highlight) => {
              if (
                highlight.highlightId !== highlightId
              ) {
                return highlight;
              }

              const enteredPoints = Number(value);

              const safePoints =
                Number.isNaN(enteredPoints)
                  ? ""
                  : enteredPoints;

              const maxPoints = Number(
                highlight.maxPoints || 0
              );

              return {
                ...highlight,
                pointsAwarded:
                  safePoints === ""
                    ? ""
                    : Math.min(
                        Math.max(safePoints, 0),
                        maxPoints
                      ),
              };
            }
          );

        return {
          ...score,
          checkedHighlights: updatedHighlights,
        };
      })
    );
  }

  return (
    <div>
        <div
        className={`alert ${
            currentTotal > maxBallotPoints ? "alert-danger" : "alert-info"
        }`}
        >
        <strong>Current Score:</strong> {currentTotal} / {maxBallotPoints}
        <br />
        <strong>Points Remaining:</strong> {pointsRemaining}
        </div>

      {scorecard.map((score) => {
        // ✅ Scorecard Mode detection
        // If any highlight has maxPoints > 0, this factor should be scored by highlight/criterion.
        const isScorecardFactor = (score.checkedHighlights || []).some(
          (highlight) => Number(highlight.maxPoints || 0) > 0
        );

        // ✅ Scorecard Mode factor total
        // Adds up all scored highlight points for this factor.
        const factorTotal = (score.checkedHighlights || []).reduce(
          (sum, highlight) => sum + Number(highlight.pointsAwarded || 0),
          0
        );

        // ✅ Scorecard Mode max total
        // Adds up all highlight max points for this factor.
        const factorMax = (score.checkedHighlights || []).reduce(
          (sum, highlight) => sum + Number(highlight.maxPoints || 0),
          0
        );

        return (
          
          <div className="card mb-3" key={score.factorId}>
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>{score.factorName}</strong>
              <span className="text-muted">{score.maxPoints} pts max</span>
            </div>

            <div className="card-body">

              {!isScorecardFactor && (
                <>
                  <label className="form-label">
                    Points Awarded for {score.factorName}
                  </label>

                  <input
                    type="number"
                    className="form-control mb-3"
                    value={score.pointsAwarded}
                    min="0"
                    max={score.maxPoints}
                    disabled={disabled}
                    onChange={(e) =>
                      handlePointsChange(score.factorId, e.target.value)
                    }
                  />
                </>
              )}

              {(score.checkedHighlights || []).length > 0 && (
                <>
                  <div className="fw-bold mb-2">
                    {isScorecardFactor ? "Scoring Criteria" : "Key Highlights"}
                  </div>

                  {(score.checkedHighlights || []).map((highlight) => {
                    const isScoredCriterion = Number(highlight.maxPoints || 0) > 0;

                    return (
                      <div className="mb-2" key={highlight.highlightId}>
                        {!isScoredCriterion ? (
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`${score.factorId}-${highlight.highlightId}`}
                              checked={highlight.checked === true}
                              disabled={disabled}
                              onChange={() =>
                                handleHighlightToggle(score.factorId, highlight.highlightId)
                              }
                            />

                            <label
                              className="form-check-label"
                              htmlFor={`${score.factorId}-${highlight.highlightId}`}
                            >
                              {highlight.highlightName}
                            </label>
                          </div>
                        ) : (
                          <div className="row align-items-center">
                            <div className="col-md-8">
                              {highlight.highlightName}
                            </div>

                            <div className="col-md-4">
                              <input
                                type="number"
                                className="form-control"
                                value={highlight.pointsAwarded}
                                min="0"
                                max={highlight.maxPoints}
                                disabled={disabled}
                                onChange={(e) =>
                                  handleHighlightPointsChange(
                                    score.factorId,
                                    highlight.highlightId,
                                    e.target.value
                                  )
                                }
                              />
                              <div className="small text-muted">
                                Max {highlight.maxPoints}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {isScorecardFactor && (
                    <div className="alert alert-secondary mt-3 mb-0">
                      <strong>Factor Total:</strong> {factorTotal} / {factorMax}
                    </div>
                  )}
                </>
              )}

            </div>
          </div>

        );
      })}
    </div>
  );
}

export default PointsScorecardForm;